import { getYear } from 'date-fns';
import { PageNavigationItem } from '../../../components/Navigation/PageNavigation/PageNavigation';
import { GetSingleSpecies_entry_exampleCatchWithImage } from '../../../interfaces/graphql';
import { abbreviateNumber } from '../../../helpers/numbers/abbreviateNumber';
import { CurrentUser } from '../../../helpers/hooks/useUserInfo';
import { convertLength } from '../../../helpers/conversion';

export const SUB_LINK_IDS = {
  GENERAL_INFO: 'sp-generalInfo',
  TOP_BAITS: 'sp-topBaits',
  FISHING_SPOTS: 'sp-fishingSpots',
  FISHING_REPORTS: 'sp-fishingReports',
  BIGGEST_CATCHES: 'sp-biggestCatches',
  PRO_TIPS: 'sp-pro-tips',
  BLOG_ENTRIES: 'sp-blog-entries',
  NEARBY_SPECIES: 'nearby-species-section',
  EXPLORE_MORE: 'explore-more-section',
};

export function getSubNavLinks({
  hasBiggestCatches,
  hasFishingReports,
  hasProTips,
  hasBlogEntries,
}: {
  hasBiggestCatches: boolean;
  hasFishingReports: boolean;
  hasProTips: boolean;
  hasBlogEntries: boolean;
}): PageNavigationItem[] {
  return [
    { id: SUB_LINK_IDS.GENERAL_INFO, label: 'General info' },
    { id: SUB_LINK_IDS.TOP_BAITS, label: 'Top baits' },
    { id: SUB_LINK_IDS.FISHING_SPOTS, label: 'Fishing spots' },
    ...(hasBiggestCatches ? [{ id: SUB_LINK_IDS.BIGGEST_CATCHES, label: 'Biggest catches' }] : []),
    ...(hasFishingReports ? [{ id: SUB_LINK_IDS.FISHING_REPORTS, label: 'Fishing reports' }] : []),
    ...(hasProTips ? [{ id: SUB_LINK_IDS.PRO_TIPS, label: 'Pro tips' }] : []),
    ...(hasBlogEntries ? [{ id: SUB_LINK_IDS.BLOG_ENTRIES, label: 'Blog posts' }] : []),
    { id: SUB_LINK_IDS.NEARBY_SPECIES, label: 'Species near you' },
    { id: SUB_LINK_IDS.EXPLORE_MORE, label: 'Explore more' },
  ];
}

export function getImageTitle({
  post: {
    user: { isPremium, nickname, createdAt },
  },
}: GetSingleSpecies_entry_exampleCatchWithImage): string {
  return `${nickname}, Fishbrain ${isPremium ? 'Pro ' : ''}angler since ${getYear(
    new Date(createdAt),
  )}`;
}

export const getLoggedCatchesText = (catchCount: number): string => {
  if (catchCount === 1) {
    return `See logged catch`;
  }
  const roundedCatchCount = abbreviateNumber(catchCount);
  return `See all ${roundedCatchCount} catches`;
};

export const handleDepth = (
  minDepth: number | null,
  maxDepth: number | null,
  user: CurrentUser,
): string => {
  const min = minDepth ? convertLength(minDepth, user, true) : '?';
  const max = maxDepth ? convertLength(maxDepth, user, true) : '?';
  return `${min} - ${max}`;
};

export const getFullTextIUCNStatus = (iucnStatus: string): string => {
  switch (iucnStatus) {
    case 'LC':
      return 'Least concern';
    case 'NT':
      return 'Near threatened';
    case 'VU':
      return 'Vulnerable';
    case 'EN':
      return 'Endangered';
    case 'CR':
      return 'Critically endangered';
    case 'EW':
      return 'Extinct in the wild';
    case 'EX':
      return 'Extinct';
    case 'NE':
      return 'Not evaluated';
    case 'DD':
      return 'Data deficient';
    default:
      throw new Error(`Unhandled IUCN status: ${iucnStatus}`);
  }
};

export const getThreatToHumans = (threat: unknown): string => {
  if (typeof threat !== 'string') {
    return 'Unknown';
  }

  switch (threat) {
    case 'harmless':
      return 'Harmless';
    case 'poisonous_to_eat':
      return 'Poisonous to eat';
    case 'venomous':
      return 'Venomous';
    case 'potential_pest':
      return 'Potential pest';
    case 'reports_of_ciguatera_poisoning':
      return 'Reports of ciguatera poisoning';
    case 'traumatogenic':
      return 'Traumatogenic';
    default:
      return 'Unknown';
  }
};
